// Clients (2E-1c): list/search/filter, create/edit (optional 9-digit PAN), duplicate-name warning, deactivate/reactivate,
// details with change history. Clients are never deleted. Saves always send the complete record; new clients carry a
// client_request_id so a retried save never creates a second client.
import { useState, useMemo, useEffect } from "react";
import { DataTable, Modal, Drawer, Field, Input, TextArea, Select, Button, Notice, StatusBadge, ActionMenu, DetailList, AuditHistory } from "../ui/kit/components";
import { T, text } from "../ui/kit/tokens";
import { saveClient, loadHistory, currentUser, newRequestId, errText } from "./api";
import { panError, emailError, trim } from "./shared";
import ClientAccount from "./ClientAccount";

const blank = { name: "", client_type: "company", pan: "", contact_person: "", phone: "", email: "", address: "", notes: "", active: true };
const record = (c) => ({ id: c.id || undefined, expected_version: c.version, name: trim(c.name), client_type: c.client_type, pan: trim(c.pan) || null,
    contact_person: trim(c.contact_person) || null, phone: trim(c.phone) || null, email: trim(c.email) || null, address: trim(c.address) || null,
    notes: trim(c.notes) || null, active: c.active });
const TYPE = { company: "Company", individual: "Individual" };

function ClientForm({ initial, onClose, onSaved }) {
    const [f, setF] = useState(() => ({ ...blank, ...(initial || {}) }));
    const [requestId] = useState(newRequestId);               // one per form: retries can't create duplicates
    const [busy, setBusy] = useState(false), [err, setErr] = useState(""), [panTaken, setPanTaken] = useState("");
    const [dup, setDup] = useState("");                        // possible-duplicate message awaiting confirmation
    const set = (k) => (e) => { setF(s => ({ ...s, [k]: e.target.value })); if (k === "pan") setPanTaken(""); if (k === "name") setDup(""); };
    const errors = { name: !trim(f.name) ? "The client's name is required." : "", pan: panError(f.pan) || panTaken, email: emailError(f.email) };
    const invalid = Object.values(errors).some(Boolean);
    const save = async (duplicateOk = false) => {
        setBusy(true); setErr("");
        try {
            const p = { ...record(f), reason: initial?.id ? "Client edited" : "Client added" };
            if (!initial?.id) p.client_request_id = requestId;
            // A same-name client was already accepted when it was saved: only a NEW client or a RENAME is re-checked.
            const sameName = !!initial?.id && trim(f.name).toLowerCase() === String(initial.name).trim().toLowerCase();
            if (duplicateOk || sameName) p.duplicate_ok = true;
            onSaved(await saveClient(p));
        } catch (e) {
            if (e.code === "23P01") setDup(errText(e));
            else if (e.code === "23505") setPanTaken(errText(e));
            else setErr(errText(e));
        } finally { setBusy(false); }
    };
    return (
        <Modal open onClose={onClose} title={initial?.id ? "Edit client" : "New client"} width={600}
            footer={dup ? <><Button variant="secondary" onClick={() => setDup("")}>Go back</Button><Button onClick={() => save(true)} disabled={busy}>Save anyway</Button></>
                : <><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => save(false)} disabled={busy || invalid}>{busy ? "Saving…" : "Save"}</Button></>}>
            <div style={{ display: "grid", gap: 14 }}>
                {dup && <Notice tone="warning"><b>Possible duplicate.</b> {dup} Check the client list; save anyway only if this is a different client.</Notice>}
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
                    <Field label="Client name" required error={errors.name}><Input value={f.name} onChange={set("name")} autoFocus /></Field>
                    <Field label="Type"><Select value={f.client_type} onChange={set("client_type")}><option value="company">Company</option><option value="individual">Individual</option></Select></Field>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <Field label="PAN" hint="Optional, 9 digits" error={errors.pan}><Input inputMode="numeric" maxLength={9} value={f.pan || ""} onChange={set("pan")} /></Field>
                    <Field label="Contact person"><Input value={f.contact_person || ""} onChange={set("contact_person")} /></Field>
                    <Field label="Phone"><Input value={f.phone || ""} onChange={set("phone")} /></Field>
                    <Field label="Email" error={errors.email}><Input type="email" value={f.email || ""} onChange={set("email")} /></Field>
                </div>
                <Field label="Address"><TextArea value={f.address || ""} onChange={set("address")} /></Field>
                <Field label="Notes" hint="Internal only — not printed on documents."><TextArea value={f.notes || ""} onChange={set("notes")} /></Field>
                {err && <Notice tone="danger">{err}</Notice>}
            </div>
        </Modal>
    );
}

const FIELD_LABEL = { name: "name", client_type: "type", pan: "PAN", contact_person: "contact person", phone: "phone", email: "email", address: "address", notes: "notes" };
// Who: the Sales audit row records the auth user of each change; only Admins can change Sales data.
const who = (uid, me) => (!uid ? "the system" : me && uid === me.id ? `you${me.email ? ` (${me.email})` : ""}` : "an Admin");
function historyEvents(rows, me) {
    return rows.map(r => {
        let label = "Created";
        if (r.action === "update") {
            const b = r.before || {}, a = r.after || {};
            if (b.active !== a.active) label = a.active ? "Reactivated" : "Deactivated";
            else { const ch = Object.keys(FIELD_LABEL).filter(k => (b[k] ?? null) !== (a[k] ?? null)).map(k => FIELD_LABEL[k]); label = ch.length ? `Changed ${ch.join(", ")}` : "Updated"; }
        }
        return { at: r.at, label, by: who(r.auth_uid, me), tone: r.action === "insert" ? T.success : T.primary };
    });
}

function ClientDetails({ client, data, onClose, onEdit, onToggle }) {
    const [events, setEvents] = useState(null);
    useEffect(() => {
        let live = true;
        Promise.all([loadHistory("sales_clients", client.id), currentUser()])
            .then(([rows, me]) => { if (live) setEvents(historyEvents(rows, me)); }).catch(() => { if (live) setEvents([]); });
        return () => { live = false; };
    }, [client.id, client.version]);
    return (
        <Drawer open onClose={onClose} title={client.name} subtitle={`${TYPE[client.client_type]}${client.active ? "" : " · inactive"}`}
            actions={<ActionMenu items={[{ label: "Edit", onClick: onEdit }, { label: client.active ? "Deactivate" : "Reactivate", onClick: onToggle }]} />}>
            <DetailList items={[["PAN", client.pan || "—"], ["Contact person", client.contact_person || "—"], ["Phone", client.phone || "—"],
                ["Email", client.email || "—"], ["Address", client.address || "—"], ["Status", client.active ? "Active" : "Inactive"]]} />
            {client.notes && <div style={{ marginTop: 16 }}><div style={text.label}>Notes</div><div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{client.notes}</div></div>}
            {data && <div style={{ marginTop: 24 }}><ClientAccount client={client} data={data} /></div>}
            <div style={{ marginTop: 24 }}>
                <div style={{ ...text.label, marginBottom: 8 }}>History</div>
                {events ? <AuditHistory events={events} /> : <div style={text.caption}>Loading…</div>}
            </div>
        </Drawer>
    );
}

export default function Clients({ clients, reload, data }) {
    const [filter, setFilter] = useState("active");
    const [form, setForm] = useState(null);                  // null | {} new | client
    const [openId, setOpenId] = useState(null);
    const [err, setErr] = useState("");
    const rows = useMemo(() => clients.filter(c => filter === "all" || (filter === "active") === c.active), [clients, filter]);
    const open = clients.find(c => c.id === openId) || null;
    const toggle = async (c) => {
        setErr("");
        // Name unchanged → any same-name duplicate was already accepted (no confirmation step for a status change).
        try { await saveClient({ ...record({ ...c, active: !c.active }), duplicate_ok: true, reason: c.active ? "Client deactivated" : "Client reactivated" }); await reload(); }
        catch (e) { setErr(errText(e)); }
    };
    return (
        <div>
            {err && <div style={{ marginBottom: 12 }}><Notice tone="danger">{err}</Notice></div>}
            <DataTable rows={rows} defaultSort={{ key: "name", dir: "asc" }}
                searchText={c => [c.name, c.pan, c.phone, c.email, c.contact_person].filter(Boolean).join(" ")}
                searchPlaceholder="Search name, PAN, phone, email…"
                empty={filter === "active" ? "No active clients yet." : "No clients here."}
                filters={<Select value={filter} onChange={e => setFilter(e.target.value)} style={{ width: 140 }} aria-label="Status filter">
                    <option value="active">Active</option><option value="inactive">Inactive</option><option value="all">All</option></Select>}
                toolbarRight={<Button onClick={() => setForm({})}>New client</Button>}
                onRowClick={c => setOpenId(c.id)}
                columns={[
                    { key: "name", header: "Client", sortValue: c => c.name.toLowerCase(),
                        render: c => <div><div style={{ fontWeight: 600 }}>{c.name}</div><div style={text.caption}>{TYPE[c.client_type]}{c.contact_person ? ` · ${c.contact_person}` : ""}</div></div> },
                    { key: "pan", header: "PAN", render: c => c.pan || "—", hideOnMobile: true },
                    { key: "contact", header: "Phone / email", render: c => [c.phone, c.email].filter(Boolean).join(" · ") || "—", hideOnMobile: true },
                    { key: "status", header: "Status", render: c => <StatusBadge status={c.active ? "on" : "off"} label={c.active ? "Active" : "Inactive"} /> },
                ]}
                rowActions={c => <ActionMenu items={[{ label: "Open", onClick: () => setOpenId(c.id) }, { label: "Edit", onClick: () => setForm(c) },
                    { label: c.active ? "Deactivate" : "Reactivate", onClick: () => toggle(c) }]} />} />
            {form && <ClientForm initial={form.id ? form : null} onClose={() => setForm(null)}
                onSaved={async (c) => { setForm(null); await reload(); if (c?.id) setOpenId(c.id); }} />}
            {open && !form && <ClientDetails client={open} data={data} onClose={() => setOpenId(null)} onEdit={() => setForm(open)} onToggle={() => toggle(open)} />}
        </div>
    );
}
